import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { MessageSquare, Clock, Check, X, Send, Package, MapPin } from 'lucide-react';

const STATUS_MAP = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  vendor_quoted: { label: 'Quoted', color: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
  expired: { label: 'Expired', color: 'bg-slate-100 text-slate-500' },
};

const VendorQuotesPanel = () => {
  const { apiCall } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondModal, setRespondModal] = useState(null);
  const [form, setForm] = useState({ quote_amount: '', estimated_days: '7', vendor_notes: '' });
  const [submitting, setSubmitting] = useState(false);

  const fetchQuotes = useCallback(async () => {
    try {
      const data = await apiCall('/api/shipping/quotes');
      setQuotes(data.quotes || []);
    } catch {
      toast.error('Failed to load quote requests');
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const handleRespond = async () => {
    if (!form.quote_amount || parseFloat(form.quote_amount) <= 0) {
      toast.error('Enter a valid quote amount');
      return;
    }
    setSubmitting(true);
    try {
      await apiCall(`/api/shipping/quotes/${respondModal.id}/vendor-respond`, {
        method: 'PUT',
        body: JSON.stringify({
          quote_amount: parseFloat(form.quote_amount),
          estimated_days: parseInt(form.estimated_days) || 7,
          vendor_notes: form.vendor_notes,
        }),
      });
      toast.success('Quote sent to customer');
      setRespondModal(null);
      setForm({ quote_amount: '', estimated_days: '7', vendor_notes: '' });
      fetchQuotes();
    } catch (err) {
      toast.error(err?.message || 'Failed to respond');
    } finally {
      setSubmitting(false);
    }
  };

  const pendingCount = quotes.filter(q => q.status === 'pending').length;

  return (
    <Card data-testid="vendor-quotes-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Shipping Quote Requests
              {pendingCount > 0 && (
                <Badge className="bg-amber-500 text-white text-xs">{pendingCount} pending</Badge>
              )}
            </CardTitle>
            <CardDescription>
              Heavy or specialized shipments that require manual pricing
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-slate-500 py-4 text-center">Loading...</p>
        ) : quotes.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="no-quotes-msg">
            No quote requests yet. These appear when a customer&apos;s shipment is too heavy for standard rates.
          </p>
        ) : (
          <div className="space-y-3">
            {quotes.map((q) => {
              const st = STATUS_MAP[q.status] || STATUS_MAP.pending;
              return (
                <div
                  key={q.id}
                  className="border rounded-lg p-4 space-y-2"
                  data-testid={`quote-row-${q.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-slate-400" />
                      <span className="text-sm font-medium">{q.item_description || 'Shipment'}</span>
                      <Badge className={`text-[10px] px-1.5 py-0 ${st.color}`}>{st.label}</Badge>
                    </div>
                    <span className="text-xs text-slate-400">{new Date(q.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-slate-600">
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      <span>{q.origin_pincode || '—'} → {q.destination_pincode}</span>
                    </div>
                    <div>{q.weight_kg} kg · {q.tier_name || `Tier ${q.tier}`}</div>
                    <div className="capitalize">{q.speed} · Zone {q.zone}</div>
                    <div>Item: ₹{(q.item_price || 0).toLocaleString('en-IN')}</div>
                  </div>
                  {q.status === 'vendor_quoted' && (
                    <div className="text-sm text-blue-700 bg-blue-50 px-3 py-1.5 rounded">
                      You quoted: <strong>₹{q.vendor_quote_amount?.toLocaleString('en-IN')}</strong>
                      {q.vendor_estimated_days && ` · ${q.vendor_estimated_days} days`}
                      {q.vendor_notes && <span className="text-blue-500 ml-2">— {q.vendor_notes}</span>}
                    </div>
                  )}
                  {q.status === 'accepted' && (
                    <div className="text-sm text-green-700 bg-green-50 px-3 py-1.5 rounded flex items-center gap-1">
                      <Check className="h-3.5 w-3.5" /> Customer accepted ₹{q.vendor_quote_amount?.toLocaleString('en-IN')}
                    </div>
                  )}
                  {q.status === 'rejected' && (
                    <div className="text-sm text-red-600 bg-red-50 px-3 py-1.5 rounded flex items-center gap-1">
                      <X className="h-3.5 w-3.5" /> Customer rejected this quote
                    </div>
                  )}
                  {q.status === 'pending' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={() => setRespondModal(q)}
                      data-testid={`respond-quote-btn-${q.id}`}
                    >
                      <Send className="h-3.5 w-3.5" /> Respond with Quote
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Respond Modal */}
        <Dialog open={!!respondModal} onOpenChange={() => setRespondModal(null)}>
          <DialogContent className="sm:max-w-md" data-testid="quote-respond-modal">
            <DialogHeader>
              <DialogTitle>Submit Shipping Quote</DialogTitle>
            </DialogHeader>
            {respondModal && (
              <div className="space-y-4">
                <div className="bg-slate-50 p-3 rounded text-sm space-y-1">
                  <p><strong>{respondModal.item_description || 'Item'}</strong> · {respondModal.weight_kg} kg</p>
                  <p className="text-slate-500">
                    {respondModal.origin_pincode || '—'} → {respondModal.destination_pincode} ·
                    Zone {respondModal.zone} · {respondModal.speed}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quoteAmount">Shipping Cost (₹) *</Label>
                  <Input
                    id="quoteAmount"
                    type="number"
                    min="1"
                    value={form.quote_amount}
                    onChange={(e) => setForm(prev => ({ ...prev, quote_amount: e.target.value }))}
                    placeholder="e.g. 3500"
                    data-testid="quote-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="estDays">Estimated Delivery (days)</Label>
                  <Input
                    id="estDays"
                    type="number"
                    min="1"
                    max="30"
                    value={form.estimated_days}
                    onChange={(e) => setForm(prev => ({ ...prev, estimated_days: e.target.value }))}
                    data-testid="quote-days-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendorNotes">Notes (optional)</Label>
                  <Textarea
                    id="vendorNotes"
                    value={form.vendor_notes}
                    onChange={(e) => setForm(prev => ({ ...prev, vendor_notes: e.target.value }))}
                    placeholder="e.g. Heavy item, requires crane unloading"
                    rows={2}
                    data-testid="quote-notes-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setRespondModal(null)}>Cancel</Button>
              <Button onClick={handleRespond} disabled={submitting} data-testid="submit-quote-btn">
                {submitting ? 'Sending...' : 'Send Quote'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default VendorQuotesPanel;
